
/**
 * Custom component for default menu item display.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.5.1
 * @copyright  Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2023-04-21 (1.0.0) (jhoward)
 *      - <biminiFoodItem> copied to <default-item>.
 *
 *    2023-06-08 (1.1.0) (jhoward)
 *      - Add `layout` prop to <default-item>.
 *      - Support "price-inline" layout in <default-item>.
 *
 *    2023-06-21 (1.1.1) (jhoward)
 *      - Support "price-below" layout in <default-item>.
 *
 *    2023-07-26 (1.2.0) (jhoward)
 *      - Rename <default> to <default-item>.
 *
 *    2023-07-26 (1.3.0) (jhoward)
 *      - Support schedules in <default-item>.
 *
 *    2023-09-08 (1.3.1) (jhoward)
 *      - Move <menu-tag> under the item title in <default-item>.
 *      - CSS tweaks in <default-item>.
 *
 *    2023-09-13 (1.3.2) (jhoward)
 *      - Add hideDenomination prop to <default-item>.
 *
 *    2023-09-13 (1.3.3) (jhoward)
 *      - Add isGroupedByPrice prop to <default-item>.
 *
 *    2023-10-05 (1.3.4) (jhoward)
 *      - Add .menu--item-multi_line_price class logic to <default-item>.
 *      - Add tagLocation prop to <default-item>.
 *
 *    2023-12-20 (1.3.5) (jhoward)
 *      - Support "before-title" tagLocation prop value in <default-item>.
 *
 *    2024-07-19 (1.3.6) (jhoward)
 *      - Rename .visual_menu--tag CSS rules to .menu--tag in <default-item>.
 *      - Refactor VisualMenuDefaultItemComponent interface from <default-item> to menu defs file.
 *
 *    2025-02-11 (1.4.0) (jhoward)
 *      - Add <menu-schedules> to the <defaultVisualItem> component if in admin view.
 *
 *    2025-04-08 (1.5.0) (jhoward)
 *      - Rename <defaultVisualItem> to <defaultItem>.
 *      - Remove "visual_" prefix from class names in <defaultItem>.
 *      - Use <menu-item-price> in <defaultItem>.
 *      - Add <defaultItem>::pricingTypesToDisplay computed property.
 *      - Add <defaultItem>::getPriceLabelByPricingType() method.
 *
 *  2025-10-13 (1.5.1) (jhoward)
 *     - Change menu/item/defaultItem.vue to use pricingTypesToDisplay object length instead of pricingTypes array length to determine price display template visibility.
 *     - Update menu/item/defaultItem.vue pricingTypesToDisplay() method to return an empty object if no pricing types have a price, preventing display of empty price blocks.
 *     - Add CSS rules in menu/item/defaultItem.vue to increase vertical spacing between consecutive elements with class .menu--tag.
 */

import MenuTag from "../menu-tag.vue";
import {defineComponent, PropType} from "vue";
import {MENU_ITEM_LAYOUT, MENU_ITEM_TAG_LOCATIONS, MenuDefaultItemComponent, MenuItem} from "../defs";
import {
  computedIsScheduled,
  methodGetItemBIN,
  getPricePerType,
  methodRemoveBINFromTitle
} from "../../../helpers/menu-sections";
import MenuSchedules from "../schedule/MenuSchedules.vue";
import MenuItemPrice from "./MenuItemPrice.vue";

export default defineComponent({
  name: 'defaultItem',
  components: {
    MenuItemPrice,
    MenuSchedules,
    MenuTag
  },
  props: {
    item: {
      type: Object as PropType<MenuItem>,
      required: true
    },
    showBin: {
      type: Boolean,
      default: false
    },
    layout: {
      type: String as PropType<MENU_ITEM_LAYOUT>,
      default: ''
    },
    pricingTypes: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    hideDenomination: {
      type: Boolean,
      default: false
    },
    isGroupedByPrice: {
      type: Boolean,
      default: false
    },
    tagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: ''
    },
  },
  computed: {
    containerClasses(): { [key: string]: boolean } {
      return {
        'menu--item-highlighted': this.item.mi_highlight === 1,
        'menu--item-no_price': !this.priceToDisplay.length,
        'menu--item-no_description': !this.item.mi_description.length,
        'menu--item-price_inline': 'price-inline' === this.layout,
        'menu--item-price_header': !!this.pricingTypes.length,
        'menu--item-multi_line_price': !!this.item.mi_multi_line_price,
      };
    },
    isScheduled: computedIsScheduled,
    priceToDisplay(): string {
      if (this.hideDenomination) {
        return this.item.price_display.replace(/\$/g, '');
      }

      return this.item.price_display;
    },
    pricingTypesToDisplay() {
      const output: { [key: string]: { price: string | number, label: string | null } } = {};
      let pricingTypeMatchCount = 0;
      this.pricingTypes.forEach(pricingType => {
        const price = getPricePerType(this.item.price_display, pricingType, '', this.hideDenomination);

        if (('' + price).length) {
          pricingTypeMatchCount++;
        }

        output[pricingType] = {
          price: price,
          label: this.getPriceLabelByPricingType(pricingType, price)
        };
      });

      if (0 === pricingTypeMatchCount) {
        return {};
      }

      return output;
    }
  },
  methods: {
    getItemBIN: methodGetItemBIN,
    removeBINFromTitle: methodRemoveBINFromTitle,


    getPriceLabelByPricingType(pricingType: string, price: string | number): string | null {
      if ('' + price === '') {
        return null;
      }

      return '$' + price + ' per ' + pricingType;
    },
  }
})
